import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { TurkeyMap } from "@/components/TurkeyMap";
import { RebuildDesigner } from "@/components/RebuildDesigner";
import { MaterialsPanel } from "@/components/MaterialsPanel";
import { Building2, Layers, Activity, ShieldCheck, ShieldAlert, MapPin } from "lucide-react";

const chartStyle = { background: "#0A1128", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 };
const RISK = { Low: "#10B981", Medium: "#F59E0B", High: "#F97316", Critical: "#EF4444" };

export const EarthquakeView = ({ result, captures = [], scanId, onUpdated }) => {
  const { materials, soil, structural, risk_score, risk_level, tbdy2018, location, rebuild, simulation_curve } = result;
  const color = RISK[risk_level];

  return (
    <div className="space-y-6" data-testid="earthquake-view">
      <div className="grid md:grid-cols-3 gap-5">
        {/* Risk gauge */}
        <div className="qs-panel p-5 flex flex-col items-center justify-center" data-testid="risk-gauge">
          <ResponsiveContainer width="100%" height={160}>
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: risk_score, fill: color }]} startAngle={210} endAngle={-30}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: "#111B3D" }} dataKey="value" cornerRadius={4} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="-mt-10 text-center">
            <div className="font-mono text-3xl font-bold" style={{ color }}>{risk_score}</div>
            <div className="text-xs uppercase tracking-widest" style={{ color }}>{risk_level} Risk</div>
          </div>
          <div className="mt-6 text-xs text-slate-500 font-mono">{structural.performance_level}</div>
        </div>

        {/* Materials + soil */}
        <div className="md:col-span-2 grid grid-cols-2 gap-4">
          <Metric icon={Building2} label="Concrete" value={`${materials.concrete_strength_mpa} MPa`} sub={materials.concrete_strength_mpa < 20 ? "Below C20/25" : "Adequate"} c={materials.concrete_strength_mpa < 20 ? "#EF4444" : "#10B981"} />
          <Metric icon={Activity} label="Steel Yield" value={`${materials.steel_yield_mpa} MPa`} sub={`Corrosion ${materials.rebar_corrosion_pct}%`} c="#00FFFF" />
          <Metric icon={Layers} label="Soil Class" value={soil.soil_class} sub={`Vs30 ${soil.vs30} m/s`} c={["ZD", "ZE"].includes(soil.soil_class) ? "#F59E0B" : "#10B981"} />
          <Metric icon={Activity} label="Liquefaction" value={`${soil.liquefaction_risk}%`} sub={soil.liquefaction_risk > 50 ? "High potential" : "Moderate"} c={soil.liquefaction_risk > 50 ? "#EF4444" : "#F59E0B"} />
        </div>
      </div>

      {/* TBDY compliance */}
      <div className={`p-5 border ${tbdy2018.compliant ? "border-[#10B981]/40 bg-[#10B981]/5" : "border-[#EF4444]/40 bg-[#EF4444]/5"}`} data-testid="tbdy-card">
        <div className="flex items-center gap-2">
          {tbdy2018.compliant ? <ShieldCheck className="h-5 w-5 text-[#10B981]" /> : <ShieldAlert className="h-5 w-5 text-[#EF4444]" />}
          <span className="font-display font-semibold">TBDY 2018 — {tbdy2018.compliant ? "Compliant" : "Non-Compliant"}</span>
        </div>
        {tbdy2018.flags.length > 0 && (
          <ul className="mt-3 space-y-1.5 text-sm text-slate-300">
            {tbdy2018.flags.map((f) => <li key={f} className="flex gap-2"><span className="text-[#EF4444]">•</span>{f}</li>)}
          </ul>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Pushover simulation */}
        <div className="qs-panel p-5">
          <h4 className="font-display font-semibold mb-1">Structural Simulation</h4>
          <p className="text-xs text-slate-500 mb-4 font-mono">{result.engine} · pushover curve</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={simulation_curve}>
              <defs>
                <linearGradient id="shear" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8A2BE2" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#8A2BE2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="displacement_m" stroke="#475569" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} unit="m" />
              <YAxis stroke="#475569" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Tooltip contentStyle={chartStyle} />
              <Area type="monotone" dataKey="base_shear_kn" stroke="#8A2BE2" strokeWidth={2} fill="url(#shear)" name="Base shear (kN)" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-3 gap-2 mt-4 text-center text-xs font-mono">
            <div><div className="text-slate-500">Period</div><div className="text-[#00FFFF]">{structural.natural_period_s}s</div></div>
            <div><div className="text-slate-500">Base shear</div><div className="text-[#00FFFF]">{structural.base_shear_kn}kN</div></div>
            <div><div className="text-slate-500">Max drift</div><div className="text-[#00FFFF]">{(structural.max_drift_ratio * 100).toFixed(2)}%</div></div>
          </div>
        </div>

        {/* Map */}
        <div className="qs-panel p-5">
          <h4 className="font-display font-semibold mb-1 flex items-center gap-2"><MapPin className="h-4 w-4 text-[#F59E0B]" /> Location — {location.city}</h4>
          <p className="text-xs text-slate-500 mb-4 font-mono">{location.lat}, {location.lng}</p>
          <TurkeyMap points={[{ ...location, risk_level, risk_score, compliant: tbdy2018.compliant, project: location.city }]} captures={captures} height={280} />
        </div>
      </div>

      <MaterialsPanel durability={result.durability} components={result.risk_score_components} scanId={scanId} onUpdated={onUpdated} />
      <RebuildDesigner rebuild={rebuild} stories={structural.stories} />
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, sub, c }) => (
  <div className="qs-panel p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><Icon className="h-4 w-4" style={{ color: c }} /> {label}</div>
    <div className="font-mono text-xl font-bold mt-2" style={{ color: c }}>{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
  </div>
);
